import { useEffect, useState } from "react";

import { Callout, Spinner } from "@blueprintjs/core";

import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import ScenarioSimpleCard from "@/components/pieces/roleplays/ScenarioSimpleCard";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import {
  EvaluationCriterionFieldsFragment,
  useLinkedScenariosForCriterionQuery,
  useLinkedScenariosForItemQuery,
} from "@/graphql";
import { EvaluationCriterionItem } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

interface LinkedScenariosDialogProps {
  open: boolean;
  onClose: () => void;
  criterion?: EvaluationCriterionFieldsFragment | null;
  item?: EvaluationCriterionItem | null;
}

export function LinkedScenariosDialog({
  open,
  onClose,
  criterion,
  item,
}: LinkedScenariosDialogProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6; // Even number for 2-column grid

  // Reset page when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [open]);

  const isForCriterion = !!criterion;
  const entityName = criterion?.name || item?.name || "";

  // Call both hooks but skip the one we don't need
  const criterionQuery = useLinkedScenariosForCriterionQuery({
    variables: {
      criterionId: criterion?.id || "",
      page: currentPage,
      pageSize,
    },
    skip: !open || !criterion,
  });

  const itemQuery = useLinkedScenariosForItemQuery({
    variables: { itemId: item?.id || "", page: currentPage, pageSize },
    skip: !open || !item,
  });

  // Use the appropriate query result
  const { loading, error } = isForCriterion ? criterionQuery : itemQuery;
  const result = isForCriterion
    ? criterionQuery.data?.linkedScenariosForCriterion
    : itemQuery.data?.linkedScenariosForItem;

  const scenarios = ensureNonNullArray(result?.scenarios ?? []);

  const hasScenarios = (result?.scenarios ?? []).length > 0;
  const hasHiddenScenarios = (result?.hiddenCount ?? 0) > 0;
  const totalCount = result?.totalCount ?? 0;

  return (
    <StyledDialog
      isOpen={open}
      onClose={onClose}
      title={`Scenarios Using "${entityName}"`}
      width="650px"
    >
      <div className="p-6">
        {loading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {error && (
          <Callout intent="danger" className="mb-4">
            Failed to load scenarios. Please try again.
          </Callout>
        )}

        {result && (
          <>
            {!hasScenarios ? (
              <div className="text-center">
                <div className="text-lg font-semibold">No scenarios found</div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {scenarios.map((scenario) => (
                    <ScenarioSimpleCard
                      key={scenario.id}
                      scenario={scenario}
                      showShortDesc={true}
                    />
                  ))}
                </div>

                {totalCount > pageSize && (
                  <div className="mt-4 border-t pt-4">
                    <Paging
                      currentPage={currentPage}
                      pageSize={pageSize}
                      setCurrentPage={setCurrentPage}
                      totalRecords={totalCount}
                    />
                  </div>
                )}
              </>
            )}
            {hasHiddenScenarios && (
              <Callout intent="primary" className="mt-4">
                {result.hiddenCount} additional scenario
                {result.hiddenCount !== 1 ? "s" : ""} hidden due to permissions.
              </Callout>
            )}
          </>
        )}
      </div>
    </StyledDialog>
  );
}
